import React from "react";
import t from "../../../../../../i18n";
import { colors } from "@ui/Theme";
import formatDate from "@ui/i18n/formatDate";
import type { Options } from "@ui/Highcharts";
import { useQOI } from "../../../useQOI";
import { formatPeriodAxis, useReportingPeriodAxis } from "../../../useReportingPeriodAxis";
import { useReportingTooltipRender } from "../../../useReportingTooltipRender";
import { ReportingPointMetadata } from "../../../../../Project/Chart/PointMetadata";
import validateInterface from "@ui/lib/validateInterface";
import type { SeriesColumnOptions } from "highcharts";
import { getColumnNavigatorSeries } from "../../../utils/navigator";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { PerformanceReporting, ReportingPeriod } from "../../../../../../api/useReporting";

interface OptionParamerters {
  reporting: PerformanceReporting;
  reportingPeriod: ReportingPeriod | null;
  series: Highcharts.SeriesOptionsType[];
}

export function useOptions(
  chart: React.RefObject<Highcharts.Chart>,
  { reporting, reportingPeriod, series }: OptionParamerters,
) {
  const translations = useTranslations();
  const [qoi] = useQOI(reporting);
  const initialXAxis = useReportingPeriodAxis(chart, reporting, reportingPeriod);

  const renderTooltip = useReportingTooltipRender();

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const points = this.points ?? [];

      const { projectCount, filledCount } = validateInterface(
        ReportingPointMetadata,
        points[0]?.point.custom,
      );

      const serieValues = points.map(p => ({
        name: p.series.name,
        color: p.color as string,
        value: p.y,
        unit: qoi?.unit ?? "",
      }));
      const projectPercent =
        (projectCount / (reporting ? Object.keys(reporting.projectById).length : 1)) * 100;
      return renderTooltip({
        title: formatDate(this.x, "month-date", null),
        subtitle: t(
          "reporting.fleet_performance.tooltip.project_subtitle",
          projectCount,
          filledCount ?? 0,
          Number(projectPercent.toFixed(1)),
        ),
        values: [serieValues],
      });
    },
    [renderTooltip, reporting, qoi?.unit],
  );

  return React.useMemo(
    (): Options => ({
      initial: {
        xAxis: initialXAxis,
      },
      chart: {
        ignoreHiddenSeries: true,
        zoomType: "x",
        marginTop: 5,
        type: "column",
        animation: true,
      },
      tooltip: {
        borderColor: colors.raw.titanium500,
        shared: true,
        formatter: constantTooltipFormatter,
      },
      series,
      xAxis: {
        crosshair: true,
        // NOTE: Prevent navigator period overflow, but disable animation
        // startOnTick: true,
      },
      yAxis: {
        id: "performance-internal-losses",
        minPadding: 0.1,
        title: {
          text: t("reporting.fleet_performance.chart.internal_losses", translations),
          textAlign: "center",
          useHTML: true,
        },
        labels: {
          unit: qoi?.unit ?? "",
          sign: false,
          useHTML: true,
        },
      },
      navigator: {
        enabled: true,
        xAxis: { labels: { formatter: formatPeriodAxis } },
        series: getColumnNavigatorSeries(series as SeriesColumnOptions[]),
      },
    }),
    [constantTooltipFormatter, initialXAxis, series, translations, qoi?.unit],
  );
}
